import React, { useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import FontAwesomeIcon from 'src/components/Icon';
import {
  IAvailableServiceTimeDay,
  IAvailableServiceTimeframe,
} from 'src/services/api/UserManagement/apiContracts';
import './service-request-timeslot-picker.scss';

interface ServiceRequestTimeslotPickerProps {
  availableServiceTimes: IAvailableServiceTimeDay[];
  setFormData: (formData: { scheduledAt: string }) => void;
}

const SLOT_TIME_FORMAT: string = 'YYYY-MM-DD HH:mm A';

const WEEKEND_DAYS: string[] = ['Saturday', 'Sunday'];

const isWeekend = (dayOfWeek: string): boolean =>
  WEEKEND_DAYS.includes(dayOfWeek);

const TIMEFRAME_ICON_BY_NAME: Record<string, string> = {
  Morning: 'sun',
  Afternoon: 'cloud-sun',
  Evening: 'moon',
};

const DEFAULT_TIMEFRAME_ICON: string = 'clock';

const timeframeIcon = (timeframeName: string): string =>
  TIMEFRAME_ICON_BY_NAME[timeframeName] ?? DEFAULT_TIMEFRAME_ICON;

const ServiceRequestTimeslotPicker = ({
  availableServiceTimes,
  setFormData,
}: ServiceRequestTimeslotPickerProps) => {
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const slotClassName = (slot: string): string =>
    slot === selectedSlot ? 'picker-slot is-selected' : 'picker-slot';

  const onSlotClick = (slot: string) => {
    setSelectedSlot(slot);
    setFormData({ scheduledAt: slot });
  };

  return (
    <div className="service-request-timeslot-picker">
      <div className="picker-title">Open timeslots</div>
      <div className="picker-week">

        {availableServiceTimes.map(
          (availableServiceTime: IAvailableServiceTimeDay) => (
            <div
              key={availableServiceTime.dayOfWeek}
              className={classNames('picker-day-column', {
                'is-weekend': isWeekend(availableServiceTime.dayOfWeek),
              })}
            >
              <div className="picker-day">
                {availableServiceTime.dayOfWeek}
              </div>

              <div className="picker-timeframes">
                {availableServiceTime.timeframes.map(
                  (timeframe: IAvailableServiceTimeframe) => (
                    <div key={timeframe.name} className="picker-timeframe">
                      <div className="picker-timeframe-heading">
                        <FontAwesomeIcon
                          glyph={timeframeIcon(timeframe.name)}
                          weight="regular"
                        />
                        <span>{timeframe.name}</span>
                      </div>

                      <div className="picker-slots">
                        {timeframe.slots.map((slot: string) => (
                          <button
                            key={slot}
                            className={slotClassName(slot)}
                            onClick={(event: React.MouseEvent) => {
                              event.preventDefault();
                              onSlotClick(slot);
                            }}
                          >
                            <div className="picker-bullet">
                              <FontAwesomeIcon glyph="check" weight="regular" />
                            </div>
                            <div className="picker-slot-details">
                              <div className="picker-slot-date">
                                {moment(slot, SLOT_TIME_FORMAT).format(
                                  'MMMM D'
                                )}
                              </div>
                              <div className="picker-slot-time">
                                {moment(slot, SLOT_TIME_FORMAT).format(
                                  'h:mm A'
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                    </div>
                  )
                )}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ServiceRequestTimeslotPicker;